import {
  addRenewVisaDocumentCreationGroupListApi,
  useGetRenewVisaDocumentGroupInfo,
  useGetRenewVisaDocumentList,
} from "@/api/services/main/renew-visa";
import { Modal } from "@/components/elements/Modal";
import { Table } from "@/components/elements/Table";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { useMakeRenewVisaColumns } from "@/features/renew-visa/hook/useMakeRenewVisaColumns";
import { useSelectionTable } from "@/hooks/useSelectionTable";
import { RENEW_VISA_DOCUMENT_STATUS } from "@/types/enum";
import { showError } from "@/utils/error";
import { toNumber } from "@/utils/number";
import { toast } from "@/utils/toast";
import { Box } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { FC, useMemo } from "react";

type TAddMoreRenewVisaDocumentModalProps = {
  isOpen: boolean;
  renewVisaDocumentStepGroupId: number;
  onClose: () => void;
  existingRenewVisaDocumentIds: string[];
  existingCompanySpecificIndustryName?: string;
  existingResidenceStatus?: string;
};

export const AddMoreRenewVisaDocumentModal: FC<TAddMoreRenewVisaDocumentModalProps> = ({
  isOpen,
  onClose,
  renewVisaDocumentStepGroupId,
  existingRenewVisaDocumentIds,
  existingCompanySpecificIndustryName,
  existingResidenceStatus,
}) => {
  const queryClient = useQueryClient();

  const { data: renewVisaDocumentGroup, isLoading: isLoadingDocumentGroup } = useGetRenewVisaDocumentGroupInfo();

  const { data: employeeRenewVisaDocumentList, isLoading: isLoadingRenewVisaList } = useGetRenewVisaDocumentList({
    status: [RENEW_VISA_DOCUMENT_STATUS.NOT_CREATED],
  });

  const columns = useMakeRenewVisaColumns();

  const {
    table,
    rowSelectionKeys: selectedAddMoreRenewVisaDocumentIds,
    clearSelection,
  } = useSelectionTable({
    columns,
    rows:
      employeeRenewVisaDocumentList?.data
        .filter((item) => item.companySpecificIndustryName === existingCompanySpecificIndustryName)
        .filter((item) => item.employeeResidenceStatus === existingResidenceStatus)
        .filter((item) => !existingRenewVisaDocumentIds.includes(item.id.toString())) ?? [],
    primaryKey: "id",
    state: {
      columnVisibility: {
        action: false,
      },
    },
  });

  const handleAddMoreRenewVisaDocument = async () => {
    if (existingRenewVisaDocumentIds.length + selectedAddMoreRenewVisaDocumentIds.length > 100) {
      toast.error("一括申請は100件まで可能です。");
      return;
    }

    if (!renewVisaDocumentGroup || !renewVisaDocumentStepGroupId) return;
    try {
      const rs = await addRenewVisaDocumentCreationGroupListApi(renewVisaDocumentGroup.id, renewVisaDocumentStepGroupId, {
        renewVisaDocumentIds: selectedAddMoreRenewVisaDocumentIds.map((item) => toNumber(item)),
      });
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.RENEW_VISA_DOCUMENTS.FETCH_DOCUMENT_CREATION_GROUP] });
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.RENEW_VISA_DOCUMENTS.FETCH_APPLICATION_INFORMATION_GROUP] });
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.RENEW_VISA_DOCUMENTS.FETCH_APPLICATION_RESULT_INFORMATION_GROUP] });
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.RENEW_VISA_DOCUMENTS.FETCH_RESIDENCE_CARD_INFORMATION_GROUP] });
      toast.success(rs.message ?? API_SUCCESS_MESSAGE);
      onClose();
      clearSelection();
    } catch (error) {
      showError(error);
    }
  };

  const isLoading = useMemo(() => isLoadingRenewVisaList || isLoadingDocumentGroup, [isLoadingDocumentGroup, isLoadingRenewVisaList]);

  return (
    <Modal
      width="70vw"
      title="外国人を追加する"
      isOpen={isOpen}
      onClose={onClose}
      disabledOk={selectedAddMoreRenewVisaDocumentIds.length === 0}
      onOk={() => handleAddMoreRenewVisaDocument()}
      labelClose="キャンセル"
      labelOk="保存"
    >
      <Box paddingY={3}>
        <Table table={table} isLoading={isLoading} maxHeight="55vh" />
      </Box>
    </Modal>
  );
};
